import React, { useState, useRef, useEffect, useCallback } from 'react';
import { sendToClaude, sendLeadByEmail } from '../services/gemini';
import HCaptcha, { HCAPTCHA_SITE_KEY } from './HCaptcha';

// ─── Helpers ──────────────────────────────────────────────────────────────────
const getTime = () =>
  new Date().toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });

const INITIAL_MSG = {
  role: 'bot',
  text: 'Olá! 👋 Eu sou a **Ágata**, assistente virtual da GB Agrotech. Vou te ajudar a encontrar a solução digital certa para a sua propriedade rural. Pode me dizer seu nome?',
  time: getTime(),
};

const QUICK_REPLIES_INIT = [
  'Quero digitalizar minha propriedade',
  'Tenho interesse em IoT',
  'Preciso de software de gestão',
  'Quero consultoria de equipamentos',
];

// ─── Markdown simples (negrito) ───────────────────────────────────────────────
const renderText = (text) => {
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return parts.map((p, i) =>
    p.startsWith('**') && p.endsWith('**')
      ? <strong key={i}>{p.slice(2, -2)}</strong>
      : p
  );
};

// ─── Componente principal ─────────────────────────────────────────────────────
export default function Chatbot() {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([INITIAL_MSG]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isComplete, setIsComplete] = useState(false);
  const [showQuickReplies, setShowQuickReplies] = useState(true);
  const [hasNotif, setHasNotif] = useState(true);
  const [leadSent, setLeadSent] = useState(false);
  const pendingLeadRef = useRef(null);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  const captchaRequired = Boolean(HCAPTCHA_SITE_KEY);

  // Envia o lead (com o token do hCaptcha, se houver). O Lambda recusa sem token válido.
  const dispatchLead = useCallback((captchaToken) => {
    const history = pendingLeadRef.current;
    if (!history || leadSent) return;
    setLeadSent(true);
    sendLeadByEmail(history, captchaToken).catch((err) => {
      console.error('Falha ao enviar lead:', err);
      setLeadSent(false);
    });
  }, [leadSent]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isLoading]);

  useEffect(() => {
    if (isOpen) {
      setHasNotif(false);
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isOpen]);

  // ─── Converte messages para histórico compatível com Gemini ──────────────────
  const buildHistory = useCallback((msgs) =>
    msgs.map(m => ({ role: m.role, text: m.text })), []);

  // ─── Envia mensagem ───────────────────────────────────────────────────────────
  const sendMessage = useCallback(async (text) => {
    if (!text.trim() || isLoading || isComplete) return;
    setShowQuickReplies(false);

    const userMsg = { role: 'user', text: text.trim(), time: getTime() };
    setMessages(prev => {
      const updated = [...prev, userMsg];
      triggerGemini(updated);
      return updated;
    });
    setInput('');
  }, [isLoading, isComplete]); // eslint-disable-line

  const triggerGemini = async (msgs) => {
    setIsLoading(true);
    try {
      const history = buildHistory(msgs);
      const { text, isComplete: done } = await sendToClaude(history);

      const botMsg = { role: 'bot', text, time: getTime() };
      setMessages(prev => [...prev, botMsg]);

      if (done) {
        setIsComplete(true);
        pendingLeadRef.current = [...msgs, botMsg];
        // Sem hCaptcha configurado: envia direto. Com hCaptcha: espera o token
        // (widget renderizado no banner de conclusão).
        if (!captchaRequired) dispatchLead(undefined);
      }
    } catch (err) {
      console.error('Gemini error:', err);
      setMessages(prev => [
        ...prev,
        {
          role: 'bot',
          text: 'Tive um probleminha aqui! 😅 Por favor, tente novamente ou fale direto com nossa equipe pelo WhatsApp.',
          time: getTime(),
        },
      ]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKey = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage(input);
    }
  };

  return (
    <>
      {/* ── Botão flutuante ── */}
      <button
        className="chat-fab"
        onClick={() => setIsOpen(o => !o)}
        aria-label="Abrir chat de atendimento"
      >
        <span className="chat-fab-icon">{isOpen ? '✕' : '💬'}</span>
        {hasNotif && !isOpen && <span className="chat-fab-badge">1</span>}
      </button>

      {/* ── Janela do chat ── */}
      {isOpen && (
        <div className="chat-window" role="dialog" aria-label="Atendimento GB Agrotech">

          {/* Header */}
          <div className="chat-header">
            <div className="chat-avatar" aria-hidden>A</div>
            <div className="chat-header-info">
              <p className="chat-name">Ágata · GB Agrotech</p>
              <p className="chat-status">
                <span className="status-dot" aria-hidden />
                {isLoading ? 'Digitando...' : 'Online agora'}
              </p>
            </div>
            <button
              className="chat-close-btn"
              onClick={() => setIsOpen(false)}
              aria-label="Fechar chat"
            >✕</button>
          </div>

          {/* Progresso de triagem */}
          {isComplete && (
            <div className="triage-complete-banner">
              {captchaRequired && !leadSent ? (
                <>
                  <p>Quase lá! Confirme que você não é um robô para enviarmos seus dados:</p>
                  <HCaptcha onVerify={dispatchLead} />
                </>
              ) : (
                <>✅ Triagem concluída! Nossa equipe entrará em contato em até 24h úteis.</>
              )}
            </div>
          )}

          {/* Mensagens */}
          <div className="chat-messages" aria-live="polite">
            {messages.map((msg, i) => (
              <div key={i} className={`msg msg-${msg.role}`}>
                {msg.role === 'bot' && (
                  <div className="msg-avatar-sm" aria-hidden>A</div>
                )}
                <div className="msg-content">
                  <div className="msg-bubble">{renderText(msg.text)}</div>
                  <time className="msg-time">{msg.time}</time>
                </div>
              </div>
            ))}

            {/* Typing indicator */}
            {isLoading && (
              <div className="msg msg-bot">
                <div className="msg-avatar-sm" aria-hidden>A</div>
                <div className="msg-content">
                  <div className="msg-bubble typing-bubble">
                    <span /><span /><span />
                  </div>
                </div>
              </div>
            )}

            {/* Quick replies iniciais */}
            {showQuickReplies && !isLoading && (
              <div className="quick-replies">
                {QUICK_REPLIES_INIT.map((q, i) => (
                  <button
                    key={i}
                    className="qr-btn"
                    onClick={() => sendMessage(q)}
                  >{q}</button>
                ))}
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <div className="chat-input-area">
            <input
              ref={inputRef}
              className="chat-input"
              type="text"
              placeholder={isComplete ? 'Atendimento encerrado' : 'Digite sua mensagem...'}
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={handleKey}
              disabled={isComplete || isLoading}
              aria-label="Campo de mensagem"
            />
            <button
              className="chat-send-btn"
              onClick={() => sendMessage(input)}
              disabled={isComplete || isLoading || !input.trim()}
              aria-label="Enviar mensagem"
            >
              ➤
            </button>
          </div>

          <p className="chat-footer-note">
            Powered by GB Agrotech · Seus dados são tratados com segurança
          </p>
        </div>
      )}
    </>
  );
}
